// Presentation only: input receipts come from the caller, never generic tool activity.
import { useRef, useState } from "react";
import { useSession } from "../store/session";
import UserAttachments, { userAttachments } from "./UserAttachments";
import { parseReviewDraft } from "../lib/reviewReferences";
import ReviewText from "./ReviewText";
import { XIcon } from "./QueuedEditor";
import "./PendingQueue.css";

export type PendingInputState = "queued" | "next_turn" | "waiting" | "sending" | "added" | "read" | "uncertain" | "failed";
export interface PendingInputItem {
  waitingForSend?: boolean;
  id: string;
  userMsgId?: string;
  text?: string;
  name?: string;
  attachmentPaths?: string[];
  attachmentNames?: string[];
  state?: PendingInputState;
  detail?: string;
  canCancel?: boolean;
  canSendNow?: boolean;
  /** The words can still change (not handed to the agent yet). */
  canEdit?: boolean;
  /** Native input: when it is handed to the agent unless edited or sent now. */
  holdUntil?: number;
  /** Server-recorded priority request, not a receipt that the agent took the input. */
  urgent?: boolean;
  dispatching?: boolean;
  unconfirmed?: boolean;
  serverQueued?: boolean;
  origin?: string;
  exchangeId?: string;
}
type InputAction = (companyId: string, inputId: string) => void | Promise<void>;
export interface PendingQueueProps {
  /** Omit to use the existing next-turn queue. Controlled lists use explicit receipts. */
  items?: readonly PendingInputItem[];
  companyId?: string;
  /** Exact userMsgId (or id) keys; never infer read state from generic activity. */
  inputStateById?: Readonly<Record<string, PendingInputState>>;
  onSendNow?: InputAction;
  /** null hides cancel. Controlled lists require their own cancellation callback. */
  onCancel?: InputAction | null;
}
const stateLabels: Record<PendingInputState, string> = {
  waiting: "Waiting to send",
  queued: "Queued for next step", next_turn: "Queued for next turn",
  sending: "Sending…", added: "Added to current task", read: "Added to current task",
  uncertain: "Delivery not confirmed", failed: "Not delivered",
};
function attachmentName(path: string): string {
  const last = path.split(/[\\/]/).pop() || "Attachment";
  // Upload storage adds Date.now() + "-". Strip that exact prefix only from
  // a path-derived display label; explicit names and file identity stay intact.
  let name = last;
  try { name = decodeURIComponent(last); } catch { /* retain malformed names */ }
  return name.replace(/^\d{13}-(?=.)/, "");
}

export default function PendingQueue({ items, companyId, inputStateById, onSendNow, onCancel }: PendingQueueProps = {}) {
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const queueByCompany = useSession((s) => s.queueByCompany);
  const cancelQueued = useSession((s) => s.cancelQueued);
  const nativeInputsByCompany = useSession((s) => s.nativeInputsByCompany);
  const sendNativeNow = useSession((s) => s.sendNativeInputNow);
  const cancelNative = useSession((s) => s.cancelNativeInput);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"send" | "cancel" | null>(null);
  const [actionError, setActionError] = useState<{ key: string; message: string } | null>(null);
  const actionLock = useRef(false);
  // Every session slot is busy: the saved message waits in line and sends by itself.
  const waitingForSlot = useSession((s) => {
    const id = companyId ?? s.activeCompanyId;
    return !!id && s.sessionStatusByCompany?.[id]?.waitingForSlot === true;
  });
  const cid = companyId ?? activeCompanyId;
  if (!cid) return null;
  const nativeInputs = nativeInputsByCompany?.[cid]?.inputs ?? [];
  const nativeIds = new Set(nativeInputs.map(input => input.userMsgId));
  const waiting: readonly PendingInputItem[] = items ?? [
    ...nativeInputs.filter(input => input.state !== "absorbed" && input.state !== "cancelled").map((input): PendingInputItem => ({
      id: input.id, userMsgId: input.userMsgId, text: input.text, attachmentPaths: input.files,
      state: input.state === "enqueued" ? "queued" : input.state as PendingInputState,
      detail: input.state === "uncertain" ? "The agent may have received this input. It will not be sent again automatically." : input.error ?? undefined,
      canCancel: input.canCancel, canSendNow: input.canSendNow, urgent: input.urgent,
      canEdit: input.canEdit, holdUntil: input.holdUntil,
    })),
    ...(queueByCompany[cid] ?? []).filter(q => !q.dispatching && !nativeIds.has(q.userMsgId ?? q.id)),
  ];
  const sendNow = onSendNow ?? (items === undefined ? sendNativeNow : undefined);
  if (waiting.length === 0) return null;
  const stateFor = (q: PendingInputItem): PendingInputState =>
    inputStateById?.[q.userMsgId ?? q.id] ?? q.state ?? (q.waitingForSend ? "waiting" : q.unconfirmed ? "uncertain" : q.dispatching || q.serverQueued === false ? "sending" : "next_turn");
  const hasSendNow = !!sendNow && waiting.some((q) => (stateFor(q) === "queued" && q.canSendNow !== false || stateFor(q) === "sending" && q.canSendNow === true) && q.origin !== "client_exchange");
  async function act(key: string, kind: "send" | "cancel", action: () => void | Promise<void>) {
    if (actionLock.current) return;
    actionLock.current = true;
    setPendingKey(key);
    setPendingAction(kind);
    setActionError(null);
    try { await action(); }
    catch { setActionError({ key, message: "Couldn’t update this input. Check its status before trying again." }); }
    finally { actionLock.current = false; setPendingKey(null); setPendingAction(null); }
  }

  return (
    <section className="pendingQueue pendingQueue--inputs" aria-label="Pending inputs">
      <div className="pendingQueue__heading"><span>Inputs</span><span>{waiting.length}</span></div>
      {hasSendNow && <p className="pendingQueue__hint">Send now also sends earlier queued inputs.</p>}
      <div className="pendingQueue__list" role="region" aria-label="Input queue" tabIndex={0}>
      {waiting.map((q) => {

        const inputId = q.userMsgId ?? q.id;
        const key = `${cid}:${inputId}`;
        const state = stateFor(q);
        const awaitingIntake = state === "queued" || state === "sending";
        const sendNowRequested = awaitingIntake && q.urgent === true;
        const requestingSendNow = awaitingIntake && pendingKey === key && pendingAction === "send";
        const unsent = state === "queued" || state === "next_turn" || state === "waiting";
        // A saved send waiting in the slot line (server-queued, not yet with the agent).
        const slotWait = waitingForSlot && state === "next_turn" && q.serverQueued === true && !nativeIds.has(inputId);
        const cancel = onCancel === null ? undefined : onCancel
          ? () => onCancel(cid, inputId)
          : items === undefined ? () => nativeIds.has(inputId) ? cancelNative(cid, inputId) : cancelQueued(cid, q.id) : undefined;
        const isNative = nativeIds.has(inputId);
        // Native rows: the server decides (a typed-but-unread one can be removed too).
        const canCancel = (isNative ? q.canCancel === true : unsent && q.canCancel !== false) && !!cancel;
        const held = isNative && state === "queued" && !!q.holdUntil;
        const canSendNow = (state === "queued" && q.canSendNow !== false || state === "sending" && q.canSendNow === true) && !!sendNow && (items !== undefined || nativeIds.has(inputId));
        const attachments = q.attachmentNames?.length ? q.attachmentNames : (q.attachmentPaths ?? []).map(attachmentName);
        const label = parseReviewDraft(q.text ?? "").text.trim() || (attachments.length ? "Attached files" : "(empty message)");
        return (
          <article className="pendingQueue__card" key={q.id} data-input-state={state} aria-busy={pendingKey === key}>
            <div className="pendingQueue__meta">
              <span className="pendingQueue__name">{q.name || "You"}</span>
              <span className="pendingQueue__state" role="status">{requestingSendNow ? "Requesting Send now…" : sendNowRequested ? "Send now requested" : slotWait ? "Waiting for a slot" : held ? "Not sent yet" : stateLabels[state]}</span>
            </div>
            <div className="pendingQueue__text"><ReviewText text={label} references={parseReviewDraft(q.text ?? "").references} /></div>
            {!!q.attachmentPaths?.length && <UserAttachments attachments={userAttachments({ files: q.attachmentPaths }).map((a, index) =>
              q.attachmentNames?.[index]?.trim() ? { ...a, filename: q.attachmentNames[index].trim() } : a)} />}
            {!q.attachmentPaths?.length && attachments.length > 0 && <ul className="pendingQueue__attachments" aria-label="Attachments">
              {attachments.map((name, index) => <li key={`${index}:${name}`} title={name}>{name}</li>)}
            </ul>}
            {sendNowRequested && <p className="pendingQueue__detail">Waiting for the agent to take this input.</p>}
            {slotWait && <p className="pendingQueue__detail">Every agent slot is busy. This is saved and sends by itself when one frees up.</p>}
            {q.detail && <p className="pendingQueue__detail">{q.detail}</p>}
            {(canSendNow || canCancel) && <div className="pendingQueue__actions">
              {canSendNow && <button type="button" className="pendingQueue__sendNow"
                disabled={pendingKey !== null} onClick={() => void act(key, "send", () => sendNow!(cid, inputId))}
                aria-label={requestingSendNow ? "Requesting Send now" : sendNowRequested ? "Retry Send now, including earlier queued inputs" : "Send now, including earlier queued inputs"}
                title="Request immediate intake of this and earlier queued inputs">{requestingSendNow ? "Requesting…" : sendNowRequested ? "Retry Send now" : "Send now"}</button>}
              {canCancel && <button type="button" className="pendingQueue__cancel"
                disabled={pendingKey !== null} onClick={() => void act(key, "cancel", cancel!)}
                aria-label={state === "queued" || state === "next_turn" || state === "waiting" ? "Take this message back to the message box — it won't be sent" : "Remove this input from the list"}
                title={state === "queued" || state === "next_turn" || state === "waiting" ? "Back to the message box" : "Remove"}>
                <XIcon />
              </button>}
            </div>}
            {actionError?.key === key && <p className="pendingQueue__error" role="status">{actionError.message}</p>}
          </article>
        );
      })}
      </div>
    </section>
  );
}
